import React from 'react';

import { getRiskBadgeClass, getRiskBadgeStyle, getRiskLabel } from './phiAuditUtils';

const PHIAuditCollectionFindings = ({ flaggedCollections }) => {
  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div>
          <h6 className="fw-semibold mb-1">Collection Security Findings</h6>

          <div className="text-muted small">
            Collections containing potential PHI or exposing it through public filters.
          </div>
        </div>

        <span className="badge bg-danger">{flaggedCollections.length}</span>
      </div>

      <div className="border rounded overflow-hidden">
        {flaggedCollections.map((collection, index) => {
          if (!collection || typeof collection !== 'object') {
            return null;
          }

          const fields = Array.isArray(collection.flaggedFields) ? collection.flaggedFields : [];

          const filters = Array.isArray(collection.flaggedFilters) ? collection.flaggedFilters : [];

          if (!fields.length && !filters.length) {
            return null;
          }

          const collectionUuid = collection.collectionUuid || `collection-${index}`;

          const collectionName = collection.collectionName || 'Unnamed Collection';

          return (
            <div key={collectionUuid} className="p-3 border-bottom">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <div className="fw-semibold">{collectionName}</div>

                  {collection.collectionTitle && collection.collectionTitle !== collectionName && (
                    <div className="text-muted small">{collection.collectionTitle}</div>
                  )}
                </div>

                <span className="badge bg-light text-dark border">
                  {fields.length + filters.length} finding
                  {fields.length + filters.length !== 1 ? 's' : ''}
                </span>
              </div>

              {fields.length > 0 && (
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div>
                      <div className="fw-semibold">PHI Fields</div>

                      <div className="text-muted small">
                        Fields that may contain unencrypted PHI.
                      </div>
                    </div>

                    <span className="badge bg-danger">{fields.length}</span>
                  </div>

                  <div className="border rounded overflow-hidden">
                    {fields.map((field, fieldIndex) => {
                      if (!field || typeof field !== 'object') {
                        return null;
                      }

                      const fieldName = field.fieldName || 'Unknown field';

                      const risk = field.risk || 'medium';

                      const reason =
                        field.reason || 'Potential PHI detected based on the supplied metadata.';

                      return (
                        <div key={`${fieldName}-${fieldIndex}`} className="p-3 border-bottom">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="pe-3">
                              <div className="fw-medium">{fieldName}</div>

                              <div className="text-muted small mt-1">{reason}</div>
                            </div>

                            <span
                              className={getRiskBadgeClass(risk)}
                              style={{
                                ...getRiskBadgeStyle(risk),
                                padding: '5px 10px',
                                fontSize: '10px',
                                fontWeight: 600,
                                borderRadius: '999px',
                                display: 'inline-block',
                                lineHeight: 1,
                                color: String(risk).toLowerCase() === 'medium' ? '#212529' : '#fff',
                              }}
                            >
                              {getRiskLabel(risk)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {filters.length > 0 && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div>
                      <div className="fw-semibold">Public Filters Exposing PHI</div>

                      <div className="text-muted small">
                        Public filters that may expose sensitive information.
                      </div>
                    </div>

                    <span className="badge bg-danger">{filters.length}</span>
                  </div>

                  <div className="border rounded overflow-hidden">
                    {filters.map((filter, filterIndex) => {
                      if (!filter || typeof filter !== 'object') {
                        return null;
                      }

                      const filterName = filter.filterName || 'Unnamed Filter';

                      const risk = filter.risk || 'medium';

                      const reason =
                        filter.reason ||
                        'Potential PHI exposure detected based on the supplied metadata.';

                      const exposedPhiFields = Array.isArray(filter.exposedPhiFields)
                        ? filter.exposedPhiFields
                        : [];

                      return (
                        <div key={`${filterName}-${filterIndex}`} className="p-3 border-bottom">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="pe-3">
                              <div className="fw-medium">{filterName}</div>

                              <div className="text-muted small mt-1">{reason}</div>

                              {exposedPhiFields.length > 0 && (
                                <div className="mt-2">
                                  {exposedPhiFields.map((field, fieldIndex) => (
                                    <span
                                      key={`${String(field)}-${fieldIndex}`}
                                      className="badge bg-light text-dark border mr-1 mb-1"
                                    >
                                      {field}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            <span
                              className={getRiskBadgeClass(risk)}
                              style={{
                                ...getRiskBadgeStyle(risk),
                                padding: '5px 10px',
                                fontSize: '10px',
                                fontWeight: 600,
                                borderRadius: '999px',
                                display: 'inline-block',
                                lineHeight: 1,
                                color: String(risk).toLowerCase() === 'medium' ? '#212529' : '#fff',
                              }}
                            >
                              {getRiskLabel(risk)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PHIAuditCollectionFindings;
